import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as RadioOn } from '../../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../../assets/images/svg/radio-off.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import themeColors from '../../../assets/styles/themeColors';
import { mediaBelow } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import SfhPrimaryButton from './SfhPrimaryButton';
import SellFromHomeServiceUnavailableDrawer from './SellFromHomeServiceUnavailableDrawer';
import SellFromHomeUnverifiedAddressDrawer from './SellFromHomeUnverifiedAddressDrawer';
import SellFromHomeStateLimitationDrawer from './SellFromHomeStateLimitationDrawer';
import AddDeliveryAddress from '../Address/AddDeliveryAddress';
import AddressStatus from '../../AddressCard/AddressStatus';
import ModalBody from '../../Modal/ModalBody';
import SectionLoader from '../../Loader/SectionLoader';
import { getDropoffLocations } from '../../../request/shippingRequest';
import { getErrorCodeFromAPIResponse } from '../../../util/helpers/rejectErrorCodeHelper';
import { fetchAccountAddresses } from '../../../store/actions/account/accountActions';
import { getQuotesForOrderSellFromHome } from '../../../store/actions/orders/orderActions';
import {
  findDefaultPaymentMethod,
  mapCreditCardsToPaymentMethods,
} from './sellFromHomePaymentHelpers';
import { resolveSfhVaultSymbol } from './sellFromHomeVaultHelpers';
import {
  getCurrentAccount,
  getCurrentAccountUid,
  selectAccountAddresses,
} from '../../../store/selectors/accountSelectors';
import { selectCreditCards } from '../../../store/selectors/creditCardSelectors';
import { getProductBySymbol } from '../../../store/selectors/productsSelector';
import addressTagType, {
  ADDRESS_TAG_TYPES,
  ADDRESS_TAG_TYPES_ICONS,
} from '../../../util/enum/api/addressTagType';
import {
  countryIso3ToIso2,
  formatAddressStringInline,
} from '../../../util/helpers/addressHelper';
import useGetCountries from '../../../util/hooks/useGetCountries';
import { USA_COUNTRY_ISO3CODE } from '../../../util/constants';
import { isStateAllowedForSfh } from '../../../constants/sfhAllowedStates';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_CONTINUE_TO_REVIEW_CLICKED,
  SFH_NO_NEARBY_SHIP_CENTERS,
  SFH_CABRELLA_ERROR,
} from '../../../constants/gtmEvents';

const SELECT_ORIGIN = 'SELECT_ORIGIN';
const LIST = 'LIST';
const ADD_ADDRESS = 'ADD_ADDRESS';

const formatLocationAddress = (loc) => {
  const streetLines = Array.isArray(loc?.StreetLines)
    ? loc.StreetLines.filter(Boolean).join(', ')
    : '';
  return [
    streetLines,
    loc?.City,
    [loc?.StateOrProvinceCode, loc?.PostalCode].filter(Boolean).join(' '),
    loc?.CountryCode,
  ]
    .filter(Boolean)
    .join(', ');
};

// Label is "Home"/"Office" for those tags. "Other"-tagged addresses show their
// custom name (TagNote) — matching AddressCard — and fall back to "Other" when
// there's no custom name; unknown/untagged also falls back to "Other".
const getAddressLabel = (address) => {
  if (address?.TagType === ADDRESS_TAG_TYPES.OTHER) {
    return address?.TagNote || addressTagType[ADDRESS_TAG_TYPES.OTHER];
  }
  return (
    addressTagType[address?.TagType] || addressTagType[ADDRESS_TAG_TYPES.OTHER]
  );
};

// Home/Office icon comes from the saved address tag; fall back to the generic
// location pin when an address has no tag so the row always shows an icon.
const getAddressIcon = (address) =>
  ADDRESS_TAG_TYPES_ICONS[address?.TagType] ||
  ADDRESS_TAG_TYPES_ICONS[ADDRESS_TAG_TYPES.OTHER];

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
  padding-top: 8px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const Body = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${themeColors.colorTextSecondary};
  margin-top: 4px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const OriginBody = styled(Body)`
  line-height: 18px;
`;

const AddressIconBox = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: ${themeColors.colorGrey};

  > svg {
    width: 22px;
    height: 22px;
  }
`;

const AddressTextCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const AddressLabelRow = styled.div`
  display: flex;
  align-items: center;
`;

const AddressLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  color: ${themeColors.colorSfhTextPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const StatusBadgeWrap = styled.div`
  position: absolute;
  top: 0;
  right: 0;

  > div {
    padding: 4px;
    margin: 0;
    border-radius: 0 4px;
    background-color: ${({ $verified }) =>
      $verified
        ? `${themeColors.colorPayoutSuccess}33`
        : `${themeColors.colorWarningText}33`};
  }

  > div p {
    font-size: 14px;
    color: ${({ $verified }) =>
      $verified ? themeColors.colorTextPrimary : themeColors.colorWarningText};
  }
`;

const AddNewAddressButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  cursor: pointer;
  background: transparent;
  border: 1px dashed ${themeColors.colorBorderPrimary};
  border-radius: 4px;
  padding: 16px;
  margin-top: 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorSfhTextPrimary};

  &:focus {
    outline: none;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const AddressLineText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const SelectedAddressBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 4px;
  padding: 12px 16px;
  margin-top: 16px;
`;

const NearbyCount = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${themeColors.colorTextSecondary};
  margin-top: 16px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const OriginList = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  overflow-y: auto;
`;

const OriginCard = styled.button`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  background: ${themeColors.colorBackgroundSecondary};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? themeColors.colorTextSecondary : 'transparent'};
  border-radius: 4px;
  padding: 12px 16px;

  &:focus {
    outline: none;
  }
`;

const RadioIndicator = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  > svg {
    width: 20px;
    height: 20px;
  }

  ${({ $selected }) =>
    $selected &&
    `
    > svg circle:first-of-type {
      fill: ${themeColors.colorTextSecondary};
    }
    > svg circle:last-of-type {
      stroke: ${themeColors.colorTextSecondary};
    }
  `}
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  overflow-y: auto;
`;

const LocationRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 4px;
  padding: 12px 16px;
`;

const LocationName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  color: ${themeColors.colorSfhTextPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const LocationAddress = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 24px;
  text-align: center;

  > svg {
    width: 36px;
    height: 36px;
  }
`;

const EmptyText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  color: ${themeColors.colorTextSecondary};
  line-height: 1.5;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
  }
`;

const NeedHelpLink = styled.a`
  display: inline-block;
  margin-top: 4px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin: 0 -16px 16px;
`;

const Footer = styled.div`
  margin-top: auto;

  > button {
    width: 100%;
    font-family: ${variables.family.goldSilver}, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    color: ${themeColors.colorGrey};
  }

  > button:disabled,
  > button:disabled:hover {
    background: ${themeColors.colorGrey3};
    border-color: ${themeColors.colorGrey3};
    opacity: 1;
  }
`;

const SellFromHomeShipping = ({
  selectedHit,
  quantity,
  selectedPaymentMethod,
  setSelectedOriginAddress,
  setInitialQuote,
  setBackInterceptor,
  handleNext,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sfhGtmEvent } = useSfhGtmHook();
  const countries = useGetCountries();
  const accountUid = useSelector(getCurrentAccountUid);
  const account = useSelector(getCurrentAccount);
  const accountAddresses = useSelector(selectAccountAddresses);
  const creditCards = useSelector(selectCreditCards);
  const productBySymbol = useSelector(getProductBySymbol);

  // Users may now ship from unverified addresses too (they show an "Unverified"
  // tag and a confirm-drawer on continue), so we no longer require IsVerified.
  // PO boxes still can't be shipping origins, and SFH ships via FedEx Ship
  // Centers in the US only, so origins stay limited to non-PO-box, USA addresses.
  const eligibleAddresses = useMemo(
    () =>
      (accountAddresses || []).filter(
        (a) => !a?.IsPoBox && a?.CountryIso3Code === USA_COUNTRY_ISO3CODE,
      ),
    [accountAddresses],
  );
  const eligibleKey = eligibleAddresses.map((a) => a.AddressUid).join(',');

  const [subStep, setSubStep] = useState(SELECT_ORIGIN);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [locations, setLocations] = useState(null);
  const [isFetchingLocations, setIsFetchingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState(false);
  const [isFetchingQuote, setIsFetchingQuote] = useState(false);
  const [isServiceUnavailableOpen, setIsServiceUnavailableOpen] = useState(
    false,
  );
  const [isUnverifiedDrawerOpen, setIsUnverifiedDrawerOpen] = useState(false);
  // AddressUid the user already confirmed via "Continue Anyway"; keeps the
  // unverified drawer from re-showing on the next CTA for the same address.
  const [ackUnverifiedUid, setAckUnverifiedUid] = useState(null);
  const [isStateLimitationOpen, setIsStateLimitationOpen] = useState(false);

  // Add-New-Address sub-step bookkeeping. `postAccountAddress` refetches the
  // address list on success, so we snapshot the UIDs on file before submitting
  // and, once a new one appears, auto-select it back on the origin picker.
  const existingUidsRef = useRef(new Set());
  const pendingSelectNewRef = useRef(false);
  const addFailedRef = useRef(false);

  const vaultSymbol = useMemo(
    () => resolveSfhVaultSymbol(selectedHit, account),
    [selectedHit, account],
  );
  const hitPrice =
    selectedHit?.ProductPrice ??
    selectedHit?.SellPrice ??
    selectedHit?.BidPrice ??
    selectedHit?.PricePerUnit ??
    selectedHit?.SellPricePerUnit;
  const unitPrice = Number(hitPrice ?? productBySymbol?.ProductPrice ?? 0) || 0;

  const openServiceUnavailableDrawer = useCallback(
    (errorDetails) => {
      sfhGtmEvent(SFH_CABRELLA_ERROR, {
        sku: vaultSymbol,
        quantity,
        unit_price: unitPrice,
        total_payout: unitPrice * quantity,
        address_selected: selectedAddress?.AddressUid,
        cabrella_error_details: errorDetails,
      });
      setIsServiceUnavailableOpen(true);
    },
    [sfhGtmEvent, vaultSymbol, quantity, unitPrice, selectedAddress],
  );

  useEffect(() => {
    if (accountUid && !accountAddresses?.length) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, accountAddresses?.length, dispatch]);

  const fetchLocations = useCallback(
    (address) => {
      if (!address?.AddressUid) return;
      setIsFetchingLocations(true);
      setLocationsError(false);
      setLocations(null);
      getDropoffLocations({ addressUid: address.AddressUid })
        .then((response) => {
          const data = Array.isArray(response?.data) ? response.data : [];
          setLocations(data);
          if (data.length === 0) {
            sfhGtmEvent(SFH_NO_NEARBY_SHIP_CENTERS, {
              sku: vaultSymbol,
              quantity,
              unit_price: unitPrice,
              total_payout: unitPrice * quantity,
              address_selected: address?.AddressUid,
            });
          }
        })
        .catch((error) => {
          // GetDropOffLocations is the first Cabrella call in the SFH flow, so
          // when Cabrella is down it now surfaces here as an Errors[].Code
          // ('CabrellaServiceUnavailable') rather than an HTTP 500. Treat it the
          // same as the quote failure and open the shared service-unavailable
          // drawer (PBI 73648) instead of the empty "no drop-off locations" state.
          const errorCode = getErrorCodeFromAPIResponse(error);
          if (errorCode === 'CabrellaServiceUnavailable') {
            openServiceUnavailableDrawer(errorCode);
            return;
          }
          setLocations([]);
          setLocationsError(true);
        })
        .finally(() => setIsFetchingLocations(false));
    },
    [
      vaultSymbol,
      quantity,
      unitPrice,
      sfhGtmEvent,
      openServiceUnavailableDrawer,
    ],
  );

  // Always start on the origin picker — even with a single address on file — so
  // the user can reach "Add New Address" and choose which address to ship from.
  // Only initialise once per account: a background account refresh (e.g.
  // SET_CURRENT_ACCOUNT) wipes and re-fetches accountAddresses, which changes
  // eligibleKey/length; without this guard the effect re-ran mid-flow and
  // snapped the user back to the origin picker, clearing their selection. It
  // also must NOT re-run after an Add-New-Address refetch. Re-initialise only
  // when the account itself actually changes.
  const initializedForAccountRef = useRef(null);
  useEffect(() => {
    if (!accountAddresses?.length) return;
    if (initializedForAccountRef.current === accountUid) return;
    initializedForAccountRef.current = accountUid;
    setSubStep(SELECT_ORIGIN);
    setSelectedAddress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleKey, accountAddresses?.length, accountUid]);

  // Let the wizard header back chevron return the drop-off list or the
  // add-address form back to the origin picker before leaving this step.
  useEffect(() => {
    if (typeof setBackInterceptor !== 'function') return undefined;
    setBackInterceptor(() => {
      if (subStep === LIST || subStep === ADD_ADDRESS) {
        setSubStep(SELECT_ORIGIN);
        return true;
      }
      return false;
    });
    return () => setBackInterceptor(null);
  }, [setBackInterceptor, subStep]);

  const showDropOffLocations = () => {
    setSubStep(LIST);
    fetchLocations(selectedAddress);
  };

  // Whether the selected address still needs the unverified confirm-drawer
  // (unverified and not yet acknowledged via "Continue Anyway").
  const needsUnverifiedConfirm = () =>
    selectedAddress &&
    !selectedAddress.IsVerified &&
    ackUnverifiedUid !== selectedAddress.AddressUid;

  const handleShowDropOff = () => {
    if (!selectedAddress) return;
    if (!isStateAllowedForSfh(selectedAddress.StateRegion)) {
      setIsStateLimitationOpen(true);
      return;
    }
    if (needsUnverifiedConfirm()) {
      setIsUnverifiedDrawerOpen(true);
      return;
    }
    showDropOffLocations();
  };

  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
    if (!address.IsVerified && ackUnverifiedUid !== address.AddressUid) {
      setIsUnverifiedDrawerOpen(true);
    }
  };

  useEffect(() => {
    if (!pendingSelectNewRef.current) return;
    const added = eligibleAddresses.find(
      (a) => !existingUidsRef.current.has(a.AddressUid),
    );
    if (added) {
      handleSelectAddress(added);
      pendingSelectNewRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleKey]);

  // Snapshot the addresses already on file, then open the inline add form.
  const handleAddNewAddress = () => {
    existingUidsRef.current = new Set(
      eligibleAddresses.map((a) => a.AddressUid),
    );
    addFailedRef.current = false;
    setSubStep(ADD_ADDRESS);
  };

  const handleAddCancel = () => setSubStep(SELECT_ORIGIN);

  // postAccountAddress calls setError only on failure; both paths call
  // handleNext, so we flag failures to decide whether to stay on the form.
  const handleAddError = (error) => {
    addFailedRef.current = Boolean(error);
  };

  const handleAddDone = () => {
    if (addFailedRef.current) {
      addFailedRef.current = false;
      return;
    }
    pendingSelectNewRef.current = true;
    setSubStep(SELECT_ORIGIN);
  };

  const isSegregated = Boolean(productBySymbol?.IsSegregated);
  const activePaymentMethod = useMemo(() => {
    if (selectedPaymentMethod) return selectedPaymentMethod;
    return findDefaultPaymentMethod(
      mapCreditCardsToPaymentMethods(creditCards),
    );
  }, [selectedPaymentMethod, creditCards]);

  const goToReview = () => {
    if (typeof setSelectedOriginAddress === 'function') {
      setSelectedOriginAddress(selectedAddress);
    }
    if (typeof handleNext === 'function') handleNext();
  };

  const proceedToReview = () => {
    if (!selectedAddress) return;
    if (
      !accountUid ||
      !vaultSymbol ||
      !quantity ||
      !selectedAddress.AddressUid
    ) {
      if (typeof setInitialQuote === 'function') setInitialQuote(null);
      goToReview();
      return;
    }
    // Review shortens its first countdown by the time this request takes.
    const requestedAt = Date.now();
    setIsFetchingQuote(true);
    dispatch(
      getQuotesForOrderSellFromHome({
        getQuotesPayload: {
          accountUid,
          symbolCode: vaultSymbol,
          quantityOrAmount: quantity,
          sideType: 'Sell',
          unitType: 'Quantity',
          paymentType: activePaymentMethod?.PaymentMethodType,
          paymentTypeUid: activePaymentMethod?.PaymentMethodUid,
          addressUid: selectedAddress.AddressUid,
          isSegregated,
          addPaymentMethodData: false,
          orderEntryType: 'OrderEntry',
          isSellFromHome: true,
          dropOffCountry: countryIso3ToIso2(selectedAddress.CountryIso3Code),
          dropOffState: selectedAddress.StateRegion,
          dropOffCity: selectedAddress.City,
          dropOffZip: selectedAddress.PostalCode,
          dropOffAddress1: selectedAddress.AddressLine,
          dropOffAddress2: selectedAddress.AddressLine2,
        },
        isLoading: setIsFetchingQuote,
        onSuccess: (data) => {
          const qsi = data?.QuoteStatusItem;
          if (qsi?.ShippingFee == null || qsi?.ShippingInsuranceFee == null) {
            openServiceUnavailableDrawer('MissingShippingOrInsuranceFee');
            return;
          }
          if (typeof setInitialQuote === 'function') {
            setInitialQuote({ ...data, requestedAt });
          }
          goToReview();
        },
        onError: (error) =>
          openServiceUnavailableDrawer(getErrorCodeFromAPIResponse(error)),
      }),
    );
  };

  const handleContinueToReview = () => {
    if (!selectedAddress) return;

    sfhGtmEvent(SFH_CONTINUE_TO_REVIEW_CLICKED, {
      sku: vaultSymbol,
      quantity,
      unit_price: unitPrice,
      total_payout: unitPrice * quantity,
      address_selected: selectedAddress?.AddressUid,
    });

    // Shipping from an unverified address warrants a confirmation first —
    // unless it was already acknowledged back when the address was selected.
    if (needsUnverifiedConfirm()) {
      setIsUnverifiedDrawerOpen(true);
      return;
    }
    proceedToReview();
  };

  const handleContinueAnyway = () => {
    setIsUnverifiedDrawerOpen(false);
    if (selectedAddress) setAckUnverifiedUid(selectedAddress.AddressUid);
  };

  const supportPhone = t('sellWizard.sfhWizard.phone');
  const addressesReady = Boolean(accountAddresses?.length);
  const hasDropOffLocations = Array.isArray(locations) && locations.length > 0;

  return (
    <SectionLoader
      isLoading={isFetchingLocations || isFetchingQuote || !addressesReady}
    >
      {addressesReady && subStep === ADD_ADDRESS ? (
        <AddDeliveryAddress
          onButtonClick={handleAddCancel}
          handleNext={handleAddDone}
          setError={handleAddError}
        />
      ) : (
        <ModalBody>
          {addressesReady && subStep === SELECT_ORIGIN && (
            <>
              <Title>
                {t('sellWizard.sfhWizard.shipping.selectOriginTitle')}
              </Title>
              <OriginBody>
                {t('sellWizard.sfhWizard.shipping.selectOriginSubtext')}
              </OriginBody>
              <AddNewAddressButton
                type="button"
                onClick={handleAddNewAddress}
                data-cy="button-sfh-add-address"
              >
                {t('sellWizard.sfhWizard.shipping.addNewAddress')}
              </AddNewAddressButton>
              <OriginList data-cy="list-sfh-origin-addresses">
                {eligibleAddresses.map((address) => {
                  const isSelected =
                    selectedAddress?.AddressUid === address.AddressUid;
                  return (
                    <OriginCard
                      type="button"
                      key={address.AddressUid}
                      $selected={isSelected}
                      onClick={() => handleSelectAddress(address)}
                      data-cy={`radio-sfh-origin-${address.AddressUid}`}
                    >
                      <RadioIndicator $selected={isSelected}>
                        {isSelected ? <RadioOn /> : <RadioOff />}
                      </RadioIndicator>
                      <AddressIconBox>{getAddressIcon(address)}</AddressIconBox>
                      <AddressTextCol>
                        <AddressLabelRow>
                          {getAddressLabel(address) && (
                            <AddressLabel>
                              {getAddressLabel(address)}
                            </AddressLabel>
                          )}
                          <StatusBadgeWrap $verified={address.IsVerified}>
                            <AddressStatus isVerified={address.IsVerified} />
                          </StatusBadgeWrap>
                        </AddressLabelRow>
                        <AddressLineText>
                          {formatAddressStringInline(address, countries)}
                        </AddressLineText>
                      </AddressTextCol>
                    </OriginCard>
                  );
                })}
              </OriginList>
              <Footer>
                <Divider />
                <SfhPrimaryButton
                  disabled={!selectedAddress}
                  onClick={handleShowDropOff}
                  data-cy="button-sfh-show-dropoff"
                >
                  {t('sellWizard.sfhWizard.shipping.showDropOffLocations')}
                </SfhPrimaryButton>
              </Footer>
            </>
          )}

          {addressesReady && subStep === LIST && (
            <>
              <Title>{t('sellWizard.sfhWizard.shipping.title')}</Title>
              <Body>{t('sellWizard.sfhWizard.shipping.body')}</Body>

              <SelectedAddressBox data-cy="box-sfh-origin-address">
                <AddressIconBox>
                  {getAddressIcon(selectedAddress)}
                </AddressIconBox>
                <AddressTextCol>
                  <AddressLabelRow>
                    {getAddressLabel(selectedAddress) && (
                      <AddressLabel>
                        {getAddressLabel(selectedAddress)}
                      </AddressLabel>
                    )}
                    <StatusBadgeWrap $verified={selectedAddress?.IsVerified}>
                      <AddressStatus isVerified={selectedAddress?.IsVerified} />
                    </StatusBadgeWrap>
                  </AddressLabelRow>
                  <AddressLineText>
                    {formatAddressStringInline(selectedAddress, countries)}
                  </AddressLineText>
                </AddressTextCol>
              </SelectedAddressBox>

              {locations !== null && !locationsError && locations.length > 0 && (
                <NearbyCount data-cy="text-sfh-locations-count">
                  {t('sellWizard.sfhWizard.shipping.locationsNearby', {
                    count: locations.length,
                  })}
                </NearbyCount>
              )}

              {locations !== null &&
                (locations.length === 0 ? (
                  <EmptyState data-cy="text-sfh-no-locations">
                    <InfoIcon />
                    <EmptyText>
                      {t('sellWizard.sfhWizard.shipping.noResultsTitle')}
                    </EmptyText>
                    <NeedHelpLink
                      href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}
                    >
                      {t('sellWizard.sfhWizard.needHelp')} {supportPhone}
                    </NeedHelpLink>
                  </EmptyState>
                ) : (
                  <ResultsList data-cy="list-sfh-locations">
                    {locations.map((location, idx) => (
                      <LocationRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${location.CompanyName}-${idx}`}
                      >
                        <LocationName>{location.CompanyName}</LocationName>
                        <LocationAddress>
                          {formatLocationAddress(location)}
                        </LocationAddress>
                      </LocationRow>
                    ))}
                  </ResultsList>
                ))}

              <Footer>
                <Divider />
                <SfhPrimaryButton
                  disabled={!hasDropOffLocations}
                  onClick={handleContinueToReview}
                  data-cy="button-sfh-continue-review"
                >
                  {t('sellWizard.sfhWizard.shipping.continueToReview')}
                </SfhPrimaryButton>
              </Footer>
            </>
          )}
        </ModalBody>
      )}
      <SellFromHomeServiceUnavailableDrawer
        isOpen={isServiceUnavailableOpen}
        onClose={() => setIsServiceUnavailableOpen(false)}
      />
      <SellFromHomeUnverifiedAddressDrawer
        isOpen={isUnverifiedDrawerOpen}
        onClose={() => setIsUnverifiedDrawerOpen(false)}
        onContinueAnyway={handleContinueAnyway}
      />
      <SellFromHomeStateLimitationDrawer
        isOpen={isStateLimitationOpen}
        onClose={() => setIsStateLimitationOpen(false)}
      />
    </SectionLoader>
  );
};

SellFromHomeShipping.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Vaults: PropTypes.arrayOf(PropTypes.shape({})),
    ProductPrice: PropTypes.number,
    SellPrice: PropTypes.number,
    BidPrice: PropTypes.number,
    PricePerUnit: PropTypes.number,
    SellPricePerUnit: PropTypes.number,
  }),
  quantity: PropTypes.number,
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodType: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    PaymentMethodUid: PropTypes.string,
  }),
  setSelectedOriginAddress: PropTypes.func,
  setInitialQuote: PropTypes.func,
  setBackInterceptor: PropTypes.func,
  handleNext: PropTypes.func,
};

export default SellFromHomeShipping;
